import { Component, inject, signal, OnInit, OnDestroy } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { Router, NavigationEnd } from "@angular/router";
import {
  Subject,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  take,
  filter,
  switchMap,
  map,
  of,
  catchError,
  from,
  concatMap,
  toArray,
} from "rxjs";
import { DataDictionaryService } from "@app/core/services/data-dictionary.service";
import { CreateItemInFlightService } from "@app/core/services/create-item-in-flight.service";
import { ButtonDirective } from "@design-system/lib/button/button.directive";
import { IconComponent } from "@design-system/lib/icon/icon.component";
import { AuthService } from "@app/core/services/auth.service";
import { ExportService } from "@app/core/services/export.service";
import { ExportSelectionService } from "@app/core/services/export-selection.service";
import { ToastService } from "@app/core/services/toast.service";
import { ModalService } from "@app/core/services/modal.service";
import { LogsService } from "@app/core/services/logs.service";
import { HierarchyNode, HierarchySidebarSelection } from "@app/core/models/data-dictionary.model";
import { collectDictionaryIdsFromHierarchy } from "@app/core/utils/collect-dictionary-ids.util";
import { TreeNodeComponent } from "../tree-node/tree-node.component";
import { CreateItemModalComponent, CreateItemData } from "../create-item-modal/create-item-modal.component";
import { getApiErrorMessage } from "@app/core/utils/api-error.util";
import { MIXPANEL_EVENTS, MixpanelItemType, toMixpanelValue } from "@app/core/analytics/mixpanel-events";
import { MixPanelService } from "@app/core/analytics/mixpanel.service";

const SIDEBAR_TREE_STATE_KEY = "dd.dataDictionary.sidebarTree.v3";

interface StoredSidebarTreeStateV1 {
  v: 1;
  selectedId: number | null;
  expansionPathIds: number[];
}

interface StoredSidebarTreeStateV2 {
  v: 2;
  selected: HierarchySidebarSelection | null;
  expansionPathIds: number[];
}

type ParsedStoredSidebarPayload =
  | StoredSidebarTreeStateV1
  | StoredSidebarTreeStateV2;

@Component({
  selector: "app-sidebar",
  standalone: true,
  imports: [FormsModule, ButtonDirective, TreeNodeComponent, CreateItemModalComponent, IconComponent],
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.scss",
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly dataDictionaryService = inject(DataDictionaryService);
  private readonly createItemInFlight = inject(CreateItemInFlightService);
  private readonly exportService = inject(ExportService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(ModalService);
  private readonly logsService = inject(LogsService);
  private readonly mixPanelService = inject(MixPanelService);
  authService = inject(AuthService);
  exportSelectionService = inject(ExportSelectionService);

  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject$ = new Subject<string>();

  hierarchy = signal<HierarchyNode[]>([]);
  filteredHierarchy = signal<HierarchyNode[]>([]);
  searchQuery = signal<string>("");
  selectionTarget = signal<HierarchySidebarSelection | null>(null);
  loading = signal<boolean>(true);
  selectMode = signal<boolean>(false);
  showCreateModal = signal<boolean>(false);
  createModalParent = signal<HierarchyNode | null>(null);
  createModalDepth = signal<number>(0);

  ngOnInit(): void {
    // hierarchy$ stays alive so refreshHierarchy() emissions reach the sidebar without a page reload
    this.dataDictionaryService.hierarchy$
      .pipe(
        takeUntil(this.destroy$),
        filter(data => data.length > 0)
      )
      .subscribe({
        next: (data) => {
          this.hierarchy.set(data);
          this.loading.set(false);
          this.syncTreeExpansionToCurrentRoute();
        },
        error: (error) => {
          console.error('SidebarComponent: error getting hierarchy', error);
          this.loading.set(false);
        }
      });

    // trigger the initial HTTP fetch (result flows through hierarchy$ above)
    this.dataDictionaryService.getHierarchy().pipe(take(1)).subscribe();

    this.searchSubject$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.filterHierarchy(term);
        this.trackSidebarSearch(term);
      });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e: NavigationEnd) => {
        this.syncTreeExpansionToCurrentRoute(e.urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(term: string): void {
    this.searchSubject$.next(term);
  }

  clearSearch(): void {
    this.searchQuery.set("");
    this.filteredHierarchy.set(this.hierarchy());
  }

  private filterHierarchy(term: string): void {
    if (!term) {
      this.filteredHierarchy.set(this.hierarchy());
      return;
    }

    const lowerTerm = term.toLowerCase();
    const filtered = this.filterNodes(this.hierarchy(), lowerTerm);
    this.filteredHierarchy.set(this.applyRouteExpandIfPresent(filtered));
  }

  /**
   * DD Data dictionary - search (section sidebar), sent from the existing debounced search only.
   * The term itself is never sent; resultCount = folders and tables whose name matches the term.
   */
  private trackSidebarSearch(term: string): void {
    if (!term) return;
    const resultCount = this.countMatchingNodes(this.hierarchy(), term.toLowerCase());
    this.mixPanelService.track(MIXPANEL_EVENTS.DATA_DICTIONARY_SEARCH, {
      section: "sidebar",
      termLength: term.length,
      resultCount,
      hasResults: resultCount > 0,
    });
  }

  private countMatchingNodes(nodes: HierarchyNode[], term: string): number {
    return nodes.reduce(
      (count, node) =>
        count +
        (node.name.toLowerCase().includes(term) ? 1 : 0) +
        (node.children ? this.countMatchingNodes(node.children, term) : 0),
      0,
    );
  }

  private filterNodes(nodes: HierarchyNode[], term: string): HierarchyNode[] {
    return nodes.reduce<HierarchyNode[]>((acc, node) => {
      const nameMatches = node.name.toLowerCase().includes(term);
      const filteredChildren = node.children
        ? this.filterNodes(node.children, term)
        : [];

      if (nameMatches || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children:
            filteredChildren.length > 0 ? filteredChildren : node.children,
          expanded:
            nameMatches || filteredChildren.length > 0 ? true : node.expanded,
        });
      }

      return acc;
    }, []);
  }

  onNodeClick(node: HierarchyNode): void {
    const key: HierarchySidebarSelection = { id: node.id, type: node.type };
    this.selectionTarget.set(key);
    this.expandHierarchyForSelection(key);

    if (node.type === "dictionary") {
      this.router.navigate(["/dictionary", node.id]);
    } else {
      this.router.navigate(["/"], { queryParams: { category: node.id } });
    }
  }

  /**
   * DD Sidebar - open. Bound to the tree's (nodeClick) only, so the onNodeClick() call made by
   * onNodeToggle() when a folder is expanded with the chevron is not tracked.
   */
  trackSidebarOpen(node: HierarchyNode): void {
    this.mixPanelService.track(MIXPANEL_EVENTS.SIDEBAR_OPEN, {
      itemType: this.toMixpanelItemType(node.type),
      depth: this.dataDictionaryService.getNodeDepth(node.id, node.type),
      fromSearch: this.searchQuery().trim().length > 0,
    });
  }

  private toMixpanelItemType(type: HierarchyNode["type"]): MixpanelItemType {
    return type === "dictionary" ? "table" : "folder";
  }

  /** DD Sidebar - rename, sent once the rename API responds. */
  private trackSidebarRename(node: HierarchyNode, status: "success" | "error"): void {
    const entityType = node.type === "dictionary" ? "Product Data Dictionary" : this.getFolderEntityType(node);
    this.mixPanelService.track(MIXPANEL_EVENTS.SIDEBAR_RENAME, {
      itemType: this.toMixpanelItemType(node.type),
      entityType: toMixpanelValue(entityType),
      status,
    });
  }

  onNodeToggle(node: HierarchyNode): void {
    const willExpand =
      node.type === "category" &&
      Boolean(node.children?.length) &&
      !node.expanded;

    this.hierarchy.update((h) => this.toggleFolderWithSiblingAccordion(h, node));
    this.filteredHierarchy.update((f) => this.toggleFolderWithSiblingAccordion(f, node));
    this.persistTreeUiState();

    if (willExpand) {
      this.onNodeClick(node);
    }
  }

  /** Stable @for track when numeric ids collide across node types */
  hierarchyRowKey(n: HierarchyNode): string {
    return `${n.type}-${n.id}`;
  }

  private toggleFolderWithSiblingAccordion(
    nodes: HierarchyNode[],
    target: Pick<HierarchyNode, "id" | "type">
  ): HierarchyNode[] {
    const atLevel = nodes.findIndex(
      (c) => c.id === target.id && c.type === target.type
    );
    if (atLevel >= 0) {
      const targetNode = nodes[atLevel];
      const isExpandable =
        targetNode.type === "category" &&
        targetNode.children &&
        targetNode.children.length > 0;
      if (!isExpandable) {
        return nodes;
      }
      const newExpanded = !targetNode.expanded;
      if (!newExpanded) {
        return nodes.map((c, j) => (j === atLevel ? { ...c, expanded: false } : c));
      }
      return nodes.map((c, j) => {
        if (j === atLevel) {
          return { ...c, expanded: true };
        }
        if (c.type === "category" && c.children && c.children.length > 0) {
          return { ...c, expanded: false };
        }
        return c;
      });
    }
    return nodes.map((n) =>
      n.children
        ? { ...n, children: this.toggleFolderWithSiblingAccordion(n.children, target) }
        : n
    );
  }

  /** Path from root to target; each step includes id+type so expand logic is unambiguous. */
  private findPathToSelection(
    tree: HierarchyNode[],
    target: HierarchySidebarSelection
  ): HierarchySidebarSelection[] | null {
    for (const n of tree) {
      if (n.id === target.id && n.type === target.type) {
        return [{ id: n.id, type: n.type }];
      }
      if (n.children?.length) {
        const under = this.findPathToSelection(n.children, target);
        if (under) {
          return [{ id: n.id, type: n.type }, ...under];
        }
      }
    }
    return null;
  }

  private findFirstNodeByNumericId(tree: HierarchyNode[], id: number): HierarchyNode | null {
    for (const n of tree) {
      if (n.id === id) {
        return n;
      }
      if (n.children?.length) {
        const under = this.findFirstNodeByNumericId(n.children, id);
        if (under) {
          return under;
        }
      }
    }
    return null;
  }

  private applyExclusiveExpandPath(
    tree: HierarchyNode[],
    path: readonly HierarchySidebarSelection[]
  ): HierarchyNode[] {
    if (path.length === 0) {
      return tree;
    }
    const next = path[0];
    return tree.map((n) => {
      if (n.id === next.id && n.type === next.type) {
        if (!n.children || n.children.length === 0) {
          return n;
        }
        return {
          ...n,
          expanded: true,
          children: this.applyExclusiveExpandPath(n.children, path.slice(1)),
        };
      }
      if (n.type === "category" && n.children && n.children.length > 0) {
        return {
          ...n,
          expanded: false,
          children: this.collapseAllCategoryFolders(n.children),
        };
      }
      return n;
    });
  }

  /**
   * Prefer `?category=` over `/dictionary/:id` so navigating home with a category
   * is not overridden by a stale dictionary segment during the same navigation tick.
   */
  private getRouteTreeSelection(urlOverride?: string): HierarchySidebarSelection | null {
    const rawUrl = urlOverride ?? this.router.url;
    const tree = this.router.parseUrl(rawUrl);
    const categoryParam = tree.queryParams["category"];
    const categoryStr = Array.isArray(categoryParam)
      ? categoryParam[0]
      : categoryParam;
    if (typeof categoryStr === "string" && categoryStr.length > 0) {
      const n = Number(categoryStr);
      if (Number.isFinite(n)) {
        return { id: n, type: "category" };
      }
    }
    const dictMatch = /\/dictionary\/([^/?#]+)/.exec(rawUrl);
    if (dictMatch) {
      const n = Number(dictMatch[1]);
      return Number.isFinite(n) ? { id: n, type: "dictionary" } : null;
    }
    return null;
  }

  private expandHierarchyForSelection(target: HierarchySidebarSelection): void {
    const h = this.hierarchy();
    if (h.length === 0) {
      return;
    }
    const path = this.findPathToSelection(h, target);
    if (!path) {
      return;
    }
    const next = this.applyExclusiveExpandPath(h, path);
    this.hierarchy.set(next);
    this.syncFilteredAfterHierarchyChange();
    this.persistTreeUiState();
  }

  private applyRouteExpandIfPresent(tree: HierarchyNode[]): HierarchyNode[] {
    const sel = this.getRouteTreeSelection();
    if (!sel) {
      return tree;
    }
    const p = this.findPathToSelection(tree, sel);
    return p ? this.applyExclusiveExpandPath(tree, p) : tree;
  }

  private collapseAllCategoryFolders(nodes: HierarchyNode[]): HierarchyNode[] {
    return nodes.map((n) => {
      if (n.type === "category") {
        return {
          ...n,
          expanded: false,
          children: n.children
            ? this.collapseAllCategoryFolders(n.children)
            : n.children,
        };
      }
      if (n.children) {
        return { ...n, children: this.collapseAllCategoryFolders(n.children) };
      }
      return n;
    });
  }

  private collectExpandedCategoryPath(
    nodes: HierarchyNode[] | undefined
  ): number[] {
    if (!nodes?.length) {
      return [];
    }
    const open = nodes.find(
      (n) =>
        n.type === "category" &&
        n.expanded &&
        n.children &&
        n.children.length > 0
    );
    if (!open) {
      return [];
    }
    return [open.id, ...this.collectExpandedCategoryPath(open.children!)];
  }

  private parseNumericIds(ids: unknown[]): number[] {
    const toId = (v: unknown): number | null => {
      if (typeof v === "number" && Number.isFinite(v)) {
        return v;
      }
      if (typeof v === "string" && /^\d+$/.test(v)) {
        return Number(v);
      }
      return null;
    };
    return ids.map((id) => toId(id)).filter((n): n is number => n !== null);
  }

  private readStoredSidebarPayload(): ParsedStoredSidebarPayload | null {
    if (typeof localStorage === "undefined") {
      return null;
    }
    try {
      const raw = localStorage.getItem(SIDEBAR_TREE_STATE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (!Array.isArray(parsed["expansionPathIds"])) {
        return null;
      }
      const expansionPathIds = this.parseNumericIds(
        parsed["expansionPathIds"] as unknown[]
      );
      const toId = (v: unknown): number | null => {
        if (typeof v === "number" && Number.isFinite(v)) {
          return v;
        }
        if (typeof v === "string" && /^\d+$/.test(v)) {
          return Number(v);
        }
        return null;
      };

      const v = parsed["v"];
      if (v === 2) {
        let selected: HierarchySidebarSelection | null = null;
        const selUnknown = parsed["selected"];
        if (
          selUnknown &&
          typeof selUnknown === "object" &&
          !Array.isArray(selUnknown)
        ) {
          const obj = selUnknown as Record<string, unknown>;
          const sid = toId(obj["id"]);
          const ty = obj["type"];
          if (
            sid != null &&
            (ty === "category" || ty === "dictionary")
          ) {
            selected = { id: sid, type: ty };
          }
        }
        if (expansionPathIds.length === 0 && selected == null) {
          return null;
        }
        return { v: 2, selected, expansionPathIds };
      }

      if (v === 1) {
        const selectedId = toId(parsed["selectedId"]);
        if (expansionPathIds.length === 0 && selectedId == null) {
          return null;
        }
        return { v: 1, selectedId, expansionPathIds };
      }
      return null;
    } catch {
      return null;
    }
  }

  private resolvedSelectionFromPayload(
    root: HierarchyNode[],
    payload: ParsedStoredSidebarPayload | null
  ): HierarchySidebarSelection | null {
    if (!payload) {
      return null;
    }
    if (payload.v === 2) {
      return payload.selected;
    }
    if (payload.v === 1 && payload.selectedId != null) {
      const hit = this.findFirstNodeByNumericId(root, payload.selectedId);
      return hit ? { id: hit.id, type: hit.type } : null;
    }
    return null;
  }

  private tryRestoreHierarchyFromStorage(
    root: HierarchyNode[]
  ): {
    hierarchy: HierarchyNode[];
    selectionTarget: HierarchySidebarSelection | null;
  } | null {
    const raw = this.readStoredSidebarPayload();
    if (!raw) {
      return null;
    }
    const migratedSelection = this.resolvedSelectionFromPayload(root, raw);
    if (migratedSelection) {
      const p = this.findPathToSelection(root, migratedSelection);
      if (p) {
        return {
          hierarchy: this.applyExclusiveExpandPath(root, p),
          selectionTarget: migratedSelection,
        };
      }
    }
    if (
      raw.expansionPathIds.length > 0 &&
      this.isPathValidForExpand(root, raw.expansionPathIds)
    ) {
      let selectionTarget: HierarchySidebarSelection | null = null;
      if (migratedSelection) {
        const ok = this.findPathToSelection(root, migratedSelection);
        selectionTarget = ok ? migratedSelection : null;
      }
      const categoryPath: HierarchySidebarSelection[] = raw.expansionPathIds.map(
        (id) => ({ id, type: "category" as const })
      );
      return {
        hierarchy: this.applyExclusiveExpandPath(root, categoryPath),
        selectionTarget,
      };
    }
    return null;
  }

  private isPathValidForExpand(
    tree: HierarchyNode[],
    path: number[]
  ): boolean {
    let current: HierarchyNode[] = tree;
    for (const id of path) {
      const node = current.find((n) => n.id === id && n.type === "category");
      if (!node || !node.children) {
        return false;
      }
      current = node.children;
    }
    return true;
  }

  private persistTreeUiState(): void {
    if (typeof localStorage === "undefined") {
      return;
    }
    try {
      const payload: StoredSidebarTreeStateV2 = {
        v: 2,
        selected: this.selectionTarget(),
        expansionPathIds: this.collectExpandedCategoryPath(this.hierarchy()),
      };
      localStorage.setItem(SIDEBAR_TREE_STATE_KEY, JSON.stringify(payload));
    } catch {
      // Quota, private mode
    }
  }

  private syncTreeExpansionToCurrentRoute(urlFromNavigation?: string): void {
    const routeSel = this.getRouteTreeSelection(urlFromNavigation);
    if (routeSel) {
      const h = this.hierarchy();
      if (h.length > 0) {
        const path = this.findPathToSelection(h, routeSel);
        if (!path) {
          this.selectionTarget.set(null);
          this.hierarchy.set(this.collapseAllCategoryFolders(h));
          this.syncFilteredAfterHierarchyChange();
          this.persistTreeUiState();
          void this.router.navigate(["/"], { queryParams: {} });
          return;
        }
      }
      this.selectionTarget.set(routeSel);
      this.expandHierarchyForSelection(routeSel);
      return;
    }

    const root = this.hierarchy();
    if (root.length === 0) {
      this.syncFilteredAfterHierarchyChange();
      return;
    }

    const restored = this.tryRestoreHierarchyFromStorage(root);
    if (restored) {
      this.hierarchy.set(restored.hierarchy);
      this.selectionTarget.set(restored.selectionTarget);
    } else {
      this.hierarchy.set(this.collapseAllCategoryFolders(root));
      this.selectionTarget.set(null);
    }
    this.syncFilteredAfterHierarchyChange();
    this.persistTreeUiState();
  }

  private syncFilteredAfterHierarchyChange(): void {
    if (this.searchQuery().trim()) {
      this.filterHierarchy(this.searchQuery());
    } else {
      this.filteredHierarchy.set(this.hierarchy());
    }
  }

  /**
   * Removes a deleted node from the persisted tree UI snapshot so reload does not
   * restore a non-existent folder/table (selected key includes id + type).
   */
  private pruneSidebarStorageAfterNodeDeletion(
    removed: Pick<HierarchyNode, "id" | "type">
  ): void {
    if (typeof localStorage === "undefined") {
      return;
    }
    try {
      const raw = this.readStoredSidebarPayload();
      if (!raw) {
        return;
      }
      const newPath = raw.expansionPathIds.filter((id) => id !== removed.id);
      let newSel: HierarchySidebarSelection | null = null;
      let newSelectedIdLegacy: number | null = null;
      if (raw.v === 2) {
        newSel =
          raw.selected &&
          !(raw.selected.id === removed.id && raw.selected.type === removed.type)
            ? raw.selected
            : null;
      } else {
        newSelectedIdLegacy =
          raw.selectedId === removed.id ? null : raw.selectedId;
      }

      const emptyPersist =
        raw.v === 2
          ? !newSel && newPath.length === 0
          : newSelectedIdLegacy == null && newPath.length === 0;
      if (emptyPersist) {
        localStorage.removeItem(SIDEBAR_TREE_STATE_KEY);
        return;
      }

      const payload: ParsedStoredSidebarPayload =
        raw.v === 2
          ? {
              v: 2,
              selected: newSel,
              expansionPathIds: newPath,
            }
          : {
              v: 1,
              selectedId: newSelectedIdLegacy,
              expansionPathIds: newPath,
            };

      localStorage.setItem(SIDEBAR_TREE_STATE_KEY, JSON.stringify(payload));
    } catch {
      // Quota, private mode
    }
  }

  /**
   * If the current route points at the node being removed, go to home or the parent category list.
   */
  private async navigateAwayIfCurrentRouteIsDeletedNode(
    node: HierarchyNode
  ): Promise<void> {
    const routeSel = this.getRouteTreeSelection();
    if (
      !routeSel ||
      routeSel.id !== node.id ||
      routeSel.type !== node.type
    ) {
      return;
    }
    if (node.type === "dictionary") {
      if (node.parentId) {
        await this.router.navigate(["/"], {
          queryParams: { category: node.parentId },
        });
      } else {
        await this.router.navigate(["/"], { queryParams: {} });
      }
      return;
    }
    if (node.type === "category") {
      if (node.parentId) {
        await this.router.navigate(["/"], {
          queryParams: { category: node.parentId },
        });
      } else {
        await this.router.navigate(["/"], { queryParams: {} });
      }
    }
  }

  private getFolderEntityType(node: HierarchyNode): string {
    const depth = this.dataDictionaryService.getNodeDepth(node.id, "category");
    return depth === 0 ? 'Category Folder' : 'Product Agrupation Folder';
  }

  async onNodeEdit(node: HierarchyNode): Promise<void> {
    const newName = await this.modalService.prompt("Rename Item", "Enter new name:", node.name);
    if (newName && newName !== node.name) {
      const oldName = node.name;
      const afterRename = (): void => {
        this.trackSidebarRename(node, "success");
        const entityType = node.type === 'dictionary' ? 'Product Data Dictionary' : this.getFolderEntityType(node);
        this.logsService.addLog({
          action: 'UPDATE',
          entity: entityType,
          entityId: node.id,
          user: this.authService.currentUser()?.email || 'unknown',
          description: `Renamed ${node.type === 'dictionary' ? 'dictionary' : 'folder'} "${oldName}" to "${newName}"`
        });
        void this.refreshHierarchy();
      };

      // The error callback only tracks the failure and rethrows, so the error is still reported
      // as unhandled exactly as before (RxJS reportUnhandledError).
      const onRenameError = (err: unknown): never => {
        this.trackSidebarRename(node, "error");
        throw err;
      };

      if (node.type === "dictionary") {
        this.dataDictionaryService
          .updateDictionary(node.id, { name: newName })
          .subscribe({ next: () => afterRename(), error: onRenameError });
      } else {
        this.dataDictionaryService
          .updateHierarchyNode({ id: node.id, type: node.type }, { name: newName })
          .subscribe({ next: () => afterRename(), error: onRenameError });
      }
    }
  }

  async onNodeDelete(node: HierarchyNode): Promise<void> {
    const itemType = this.toMixpanelItemType(node.type);
    const depth = this.dataDictionaryService.getNodeDepth(node.id, node.type);
    if (node.type === 'category' && node.children && node.children.length > 0) {
      this.mixPanelService.track(MIXPANEL_EVENTS.SIDEBAR_DELETE, {
        itemType,
        depth,
        status: 'blocked',
        blockedReason: 'folder not empty',
      });
      this.toastService.error('Cannot delete a folder that contains items. Please delete the contents first.');
      return;
    }
    
    const confirmed = await this.modalService.confirm(
      "Delete Item",
      `Are you sure you want to delete "${node.name}"?`
    );
    if (confirmed) {
      const afterDelete = (): void => {
        this.mixPanelService.track(MIXPANEL_EVENTS.SIDEBAR_DELETE, { itemType, depth, status: 'success' });
        this.pruneSidebarStorageAfterNodeDeletion({
          id: node.id,
          type: node.type,
        });
        void (async () => {
          await this.navigateAwayIfCurrentRouteIsDeletedNode(node);
          this.refreshHierarchy();
        })();
      };

      // The error callback only tracks the failure and rethrows, so the error is still reported
      // as unhandled exactly as before (RxJS reportUnhandledError).
      const onDeleteError = (err: unknown): never => {
        this.mixPanelService.track(MIXPANEL_EVENTS.SIDEBAR_DELETE, { itemType, depth, status: 'error' });
        throw err;
      };

      if (node.type === 'dictionary') {
        this.dataDictionaryService
          .deleteDictionary(node.id)
          .subscribe({ next: () => afterDelete(), error: onDeleteError });
      } else {
        this.dataDictionaryService
          .deleteHierarchyNode({ id: node.id, type: node.type })
          .subscribe({ next: () => afterDelete(), error: onDeleteError });
      }
    }
  }

  openCreateModal(parentNode: HierarchyNode | null = null): void {
    this.createItemInFlight.clear();
    const depth = parentNode
      ? this.dataDictionaryService.getNodeDepth(parentNode.id, parentNode.type) + 1
      : 0;
    this.createModalParent.set(parentNode);
    this.createModalDepth.set(depth);
    this.showCreateModal.set(true);
  }

  closeCreateModal(): void {
    this.createItemInFlight.clear();
    this.showCreateModal.set(false);
    this.createModalParent.set(null);
  }

  onCreateItem(data: CreateItemData): void {
    const user = this.authService.currentUser()?.email || 'unknown';
    // DD Sidebar - create: modal context captured up front (closeCreateModal() resets it).
    const hasCsvFields = !!data.csvFields && data.csvFields.length > 0;
    let creationMethod: 'none' | 'csv' | 'scratch' = 'scratch';
    if (data.type === 'folder') creationMethod = 'none';
    else if (hasCsvFields) creationMethod = 'csv';
    const createProps = {
      itemType: data.type === 'folder' ? 'folder' : 'table',
      creationMethod,
      origin: this.createModalParent() ? 'node add child' : 'sidebar header',
      depth: this.createModalDepth(),
    };
    const trackCreate = (status: 'success' | 'error' | 'partial', importedFields?: number): void => {
      this.mixPanelService.track(MIXPANEL_EVENTS.SIDEBAR_CREATE, {
        ...createProps,
        ...(importedFields !== undefined ? { importedFields } : {}),
        status,
      });
    };

    if (data.type === 'folder') {
      this.dataDictionaryService
        .createFolderWithNode(data.parentId, data.name)
        .subscribe({
          next: () => {
            trackCreate('success');
            this.refreshHierarchy();
            this.toastService.success('Folder created successfully');
            this.closeCreateModal();
          },
          error: (err) => {
            trackCreate('error');
            this.createItemInFlight.setError(getApiErrorMessage(err));
          }
        });
    } else {
      this.dataDictionaryService
        .createDictionaryWithNode(data.parentId, data.name, data.initialField)
        .subscribe({
          next: (result) => {
            if (data.initialField) {
              const firstField = result.dictionary.fields?.[0];
              this.logsService.addLog({
                action: 'CREATE',
                entity: 'Field of a Data Dictionary',
                entityId: firstField?.id ?? 'new',
                user,
                description: `Added field "${data.initialField.fieldName}" to "${data.name}"`
              });
            }

            if (data.csvFields && data.csvFields.length > 0) {
              this.dataDictionaryService
                .importDictionaryFields(result.dictionary.id, data.csvFields)
                .subscribe({
                  next: (updatedDict) => {
                    trackCreate('success', updatedDict.fields?.length ?? 0);
                    this.logsService.addLog({
                      action: 'IMPORT',
                      entity: 'Product Data Dictionary',
                      entityId: result.dictionary.id,
                      user,
                      description: `Imported ${updatedDict.fields?.length ?? 0} fields from CSV into "${data.name}"`
                    });
                    this.afterCreateTableThenNavigateToIt(
                      result.dictionary.id,
                      () => {
                        this.toastService.success(
                          'Table created and fields imported successfully'
                        );
                        this.closeCreateModal();
                      }
                    );
                  },
                  error: () => {
                    trackCreate('partial', 0);
                    this.afterCreateTableThenNavigateToIt(
                      result.dictionary.id,
                      () => {
                        this.toastService.show(
                          'Table created but failed to import fields',
                          'error'
                        );
                        this.closeCreateModal();
                      }
                    );
                  }
                });
            } else {
              trackCreate('success');
              this.afterCreateTableThenNavigateToIt(result.dictionary.id, () => {
                this.toastService.success('Table created successfully');
                this.closeCreateModal();
              });
            }
          },
          error: (err) => {
            trackCreate('error');
            this.createItemInFlight.setError(getApiErrorMessage(err));
          }
        });
    }
  }

  onNodeAddChild(node: HierarchyNode): void {
    this.openCreateModal(node);
  }

  onNodeMove(event: {
    nodeId: number;
    targetParentId: number | null;
    targetIndex: number;
    placement: 'before' | 'inside' | 'after';
    targetNodeId: number;
  }): void {
    // Analytics only: a drop onto the table's current folder is a no-op (the service skips the API call).
    const isSameParentDrop = this.findTableParentId(this.hierarchy(), event.nodeId) === event.targetParentId;
    this.dataDictionaryService.moveHierarchyNode(event.nodeId, event.targetParentId, event.targetIndex).subscribe({
      next: () => {
        this.refreshHierarchy();
        this.toastService.success('Table moved successfully');
        if (!isSameParentDrop) {
          this.trackSidebarMove(event.nodeId, event.targetParentId, 'success');
        }
      },
      error: (error: unknown) => {
        this.toastService.show(getApiErrorMessage(error), 'error');
        if (!isSameParentDrop) {
          this.trackSidebarMove(event.nodeId, event.targetParentId, 'error');
        }
      },
    });
  }

  /** Read-only lookup of a table's current parent folder id in the tree; undefined when the table is not found. */
  private findTableParentId(nodes: HierarchyNode[], tableId: number): number | null | undefined {
    for (const node of nodes) {
      if (node.type === 'dictionary' && node.id === tableId) {
        return node.parentId;
      }
      if (node.children?.length) {
        const found = this.findTableParentId(node.children, tableId);
        if (found !== undefined) {
          return found;
        }
      }
    }
    return undefined;
  }

  /** DD Sidebar - move. Only tables are draggable; targetFolderId is omitted at the root (null parent). */
  private trackSidebarMove(dictionaryId: number, targetFolderId: number | null, status: 'success' | 'error'): void {
    this.mixPanelService.track(MIXPANEL_EVENTS.SIDEBAR_MOVE, {
      dictionaryId,
      ...(targetFolderId !== null && targetFolderId !== undefined ? { targetFolderId } : {}),
      status,
    });
  }

  sortAlphabetically(): void {
    this.dataDictionaryService
      .sortHierarchyAlphabetically(null)
      .subscribe((sorted) => {
        this.setHierarchyDataFromApi(sorted);
        this.syncTreeExpansionToCurrentRoute();
      });
  }

  toggleSelectMode(): void {
    this.selectMode.update((v) => !v);
    if (!this.selectMode()) {
      this.exportSelectionService.clear();
    }
  }

  async bulkDeleteSelectedDictionaries(): Promise<void> {
    const ids = this.exportSelectionService.getSelectedIds();
    if (ids.length === 0) return;

    const n = ids.length;
    const confirmed = await this.modalService.confirm(
      'Delete Dictionaries',
      `Are you sure you want to delete ${n} dictionar${n === 1 ? 'y' : 'ies'}? This action cannot be undone.`,
    );
    if (!confirmed) return;

    const currentRoute = this.getRouteTreeSelection();
    const selectedIdsSet = new Set(ids);
    const foldersToDelete = this.collectFoldersToAutoDelete(this.hierarchy(), selectedIdsSet);
    // DD Sidebar - bulk delete: folders actually deleted (deleteHierarchyNode emits true; failures map to false).
    let foldersDeleted = 0;

    this.dataDictionaryService.bulkDeleteDictionaries(ids)
      .pipe(
        take(1),
        switchMap((result) => {
          const { succeeded, failed } = result;
          succeeded.forEach((id) => {
            this.pruneSidebarStorageAfterNodeDeletion({ id, type: 'dictionary' });
          });

          if (succeeded.length > 0) {
            const s = succeeded.length;
            this.toastService.success(`${s} dictionar${s === 1 ? 'y' : 'ies'} deleted`);
          }
          if (failed.length > 0) {
            const f = failed.length;
            this.toastService.show(
              `${f} dictionar${f === 1 ? 'y' : 'ies'} could not be deleted`,
              'error',
            );
          }

          if (foldersToDelete.length === 0) return of(result);

          // Delete now-empty folders deepest-first so parent folders are empty by the time we reach them
          const orderedIds = foldersToDelete
            .sort((a, b) => b.depth - a.depth)
            .map((f) => f.id);

          return from(orderedIds).pipe(
            concatMap((folderId) =>
              this.dataDictionaryService
                .deleteHierarchyNode({ id: folderId, type: 'category' })
                .pipe(catchError(() => of(false))),
            ),
            toArray(),
            map((folderResults) => {
              foldersDeleted = folderResults.filter((deleted) => deleted === true).length;
              return result;
            }),
          );
        }),
      )
      .subscribe({
        next: ({ succeeded, failed }) => {
          this.mixPanelService.track(MIXPANEL_EVENTS.SIDEBAR_BULK_DELETE, {
            selectedCount: n,
            succeededCount: succeeded.length,
            failedCount: failed.length,
            foldersDeleted,
          });
          this.exportSelectionService.clear();
          this.selectMode.set(false);
          this.refreshHierarchy();

          if (currentRoute?.type === 'dictionary' && succeeded.includes(currentRoute.id)) {
            void this.router.navigate(['/'], { queryParams: {} });
          }
        },
        error: () => {
          this.toastService.show('Error deleting dictionaries', 'error');
        },
      });
  }

  onNodeSelect(event: { node: HierarchyNode; selected: boolean }): void {
    if (event.node.type === 'category') {
      const ids = this.getAllDictionaryIdsUnderNode(event.node);
      const anySelected = ids.some(id => this.exportSelectionService.isSelected(id));
      ids.forEach(id => {
        if (anySelected) this.exportSelectionService.deselect(id);
        else this.exportSelectionService.select(id);
      });
    } else {
      if (event.selected) this.exportSelectionService.select(event.node.id);
      else this.exportSelectionService.deselect(event.node.id);
    }
  }

  private getAllDictionaryIdsUnderNode(node: HierarchyNode): number[] {
    if (node.type === 'dictionary') return [node.id];
    return (node.children ?? []).flatMap(c => this.getAllDictionaryIdsUnderNode(c));
  }

  private willFolderBeEmpty(node: HierarchyNode, selectedIds: Set<number>): boolean {
    if (node.type === 'dictionary') return selectedIds.has(node.id);
    const children = node.children ?? [];
    if (children.length === 0) return false;
    return children.every(child => this.willFolderBeEmpty(child, selectedIds));
  }

  private collectFoldersToAutoDelete(
    nodes: HierarchyNode[],
    selectedIds: Set<number>,
    depth = 0,
  ): { id: number; depth: number }[] {
    const result: { id: number; depth: number }[] = [];
    for (const node of nodes) {
      if (node.type === 'category') {
        result.push(...this.collectFoldersToAutoDelete(node.children ?? [], selectedIds, depth + 1));
        if (this.willFolderBeEmpty(node, selectedIds)) {
          result.push({ id: node.id, depth });
        }
      }
    }
    return result;
  }

  exportSelected(): void {
    const selectedIds = this.exportSelectionService.getSelectedIds();
    if (selectedIds.length === 0) return;

    const dictionaryIds = this.collectDictionaryIds(selectedIds);

    if (dictionaryIds.length === 0) {
      this.toastService.show("No dictionaries found in selection", "error");
      return;
    }

    const observables = dictionaryIds.map((id) =>
      this.dataDictionaryService.getDictionaryById(id).pipe(take(1))
    );

    forkJoin(observables).subscribe({
      next: (dictionaries) => {
        const validDictionaries = dictionaries.filter(
          (d): d is NonNullable<typeof d> => d !== null && d !== undefined,
        );
        if (validDictionaries.length > 0) {
          void this.exportService
            .exportMultipleToExcelWorkbook(validDictionaries)
            .then(() => {
              this.toastService.show(
                `Exported ${validDictionaries.length} dictionaries`,
                "success",
              );
            })
            .catch(() => {
              this.toastService.show("Error exporting to Excel", "error");
            });
        } else {
          this.toastService.show("No valid dictionaries to export", "error");
        }
      },
      error: () => {
        this.toastService.show("Error fetching dictionaries", "error");
      }
    });
  }

  private setHierarchyDataFromApi(data: HierarchyNode[]): void {
    this.hierarchy.set(data);
    this.syncFilteredAfterHierarchyChange();
  }

  /**
   * Reloads the tree, then runs UI steps and navigates to the new table once the
   * hierarchy already includes the created node (so the sidebar can expand and select it).
   */
  private afterCreateTableThenNavigateToIt(
    dictionaryId: number,
    runAfterRefresh: () => void
  ): void {
    this.dataDictionaryService.refreshHierarchy().subscribe((data) => {
      this.setHierarchyDataFromApi(data);
      runAfterRefresh();
      void this.router.navigate(["/dictionary", dictionaryId]).then((ok) => {
        if (ok) {
          this.syncTreeExpansionToCurrentRoute();
        }
      });
    });
  }

  private collectDictionaryIds(selectedIds: number[]): number[] {
    return collectDictionaryIdsFromHierarchy(this.hierarchy(), selectedIds);
  }

  private refreshHierarchy(): void {
    this.dataDictionaryService.refreshHierarchy().subscribe((data) => {
      this.setHierarchyDataFromApi(data);
      this.syncTreeExpansionToCurrentRoute();
    });
  }
}
